import { CellContext } from '@tanstack/react-table';
import Typography from 'components/Typography/Typography';
import { getCreatorCopyString } from 'common/helpers/v1';
import { TFunction } from 'i18next';
import React, { createElement, ReactElement } from 'react';
import styled from 'styled-components';
import { useTheme } from 'styled-components';
import { DefaultTheme } from 'styled-components';
import { BaseType, TableColumnConfigs } from 'types';
import { MaintenanceCreator, MaintenanceEvent, MaintenanceEventTableRow } from 'types/maintenance';
import { getNextStepCell, getStatusCellEvent } from './helpers';

interface GroupRow extends BaseType {
  rowType: 'group';
  title: string;
  countStr?: string;
  key: string;
}
type TableRow = GroupRow | MaintenanceEventTableRow;
const GroupRowContainer = ({ data }: { data: GroupRow }): ReactElement => {
  const theme = useTheme();
  return (
    <Typography weight="medium" mb={0}>
      {data.title} <span style={{ color: theme.colors.mediumBlue }}>{data.countStr}</span>
    </Typography>
  );
};
const maybeEmptyCell = (
  data: TableRow,
  child: React.ReactNode,
  bg?: string,
  textColor?: string,
  padding?: string,
  width?: string
) => {
  let colSpan = 0;
  let children = child;

  if ((data as TableRow)?.rowType === 'group') {
    colSpan = 0;
    children = null;
  }

  return createElement(
    'p',
    {
      colSpan,
      style: {
        color: textColor,
        background: bg ? bg : 'transparent',
        margin: '0px',
        padding: padding,
        width: width || '300px'
      }
    },
    children
  );
};

const TwoStackedTextContainer = styled.div<{ width?: string }>`
  width: ${({ width }) => width || '100%'};
  margin: 0;
  p {
    margin-bottom: 0;
    word-break: break-word;
  }
  p:last-child {
    margin-top: 0.125rem;
  }
  text-align: left;
`;
const Description = styled.div<{ isEmpty?: boolean }>`
  ${({ isEmpty }) => {
    return isEmpty ? 'text-align: center; width: 7.5rem' : '';
  }}
`;
const DueDate = styled.div`
  width: 7rem;
`;

const generateColumnConfigs = ({
  theme,
  setSelectedPm,
  showSecondDescription = true,
  onClickCreateService,
  t,
  isServiceTab
}: {
  theme: DefaultTheme;
  // TODO - use again when tasks are added
  setSelectedPm?: (pm: MaintenanceEventTableRow) => void;
  showSecondDescription?: boolean;
  onClickCreateService?: (data: MaintenanceEventTableRow) => void;
  t: TFunction<'fpns'[], undefined>;
  isServiceTab?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): any => {
  return [
    {
      header: t('mm_table_machine_model') as string,
      id: 'machineDescription',
      isEnableSorting: true,
      isSelected: true,
      renderer: (value: CellContext<TableColumnConfigs, string>) => {
        const data = value.row.original;
        // If the row is of type 'group', make a cell that spans the whole width of the table
        // Otherwise return a cell of colspan 1
        let colSpan = 1;
        let children = <>{value.getValue()}</>;

        if ((data as TableRow)?.rowType === 'group') {
          colSpan = 5;
          children = <GroupRowContainer data={data as GroupRow} />;
        }
        const id = data.machineId as string;
        return createElement(
          'span',
          { colSpan, id, style: { margin: '0px', width: '15rem' } },
          children
        );
      }
    },
    {
      header: t('mm_table_sub_component') as string,
      id: 'subComponent',
      isEnableSorting: true,
      isSelected: true,
      renderer: (value: CellContext<TableColumnConfigs, string>) => {
        const data = value.row.original;
        // TODO - provide subcomponent when available from backend
        return maybeEmptyCell(data as TableRow, value.getValue(), '', '', '', 'auto');
      }
    },
    ...(isServiceTab
      ? [
          {
            header: t('priority') as string,
            id: 'priority',
            isEnableSorting: true,
            isSelected: true,
            renderer: (value: CellContext<TableColumnConfigs, string>) => {
              return <div className="priority-text">{value.getValue()}</div>;
            }
          }
        ]
      : []),
    {
      header: t('mm_table_description') as string,
      id: 'description',
      isEnableSorting: false,
      isSelected: true,
      renderer: (value: CellContext<TableColumnConfigs, string>) => {
        const data = value.row.original;
        return maybeEmptyCell(
          data as TableRow,
          <TwoStackedTextContainer>
            <Typography>{value.getValue()}</Typography>
            {showSecondDescription && (
              <Typography
                variant="body2"
                color={
                  data.creator === MaintenanceCreator.Predictive
                    ? theme.colors.darkRed
                    : theme.colors.black
                }
              >
                {getCreatorCopyString(data as MaintenanceEvent)}
              </Typography>
            )}
          </TwoStackedTextContainer>
        );
      }
    },
    ...(isServiceTab
      ? [
          {
            header: t('due_date') as string,
            id: 'dueDate',
            isEnableSorting: true,
            isSelected: true,
            renderer: (value: CellContext<TableColumnConfigs, string>) => {
              const data = value.row.original;
              return <DueDate>{String(data?.suggestedDue)}</DueDate>;
            }
          },
          {
            header: 'Service Status',
            id: 'serviceStatus',
            isEnableSorting: true,
            isSelected: true,
            renderer: (value: CellContext<TableColumnConfigs, string>) => {
              return <div>{value.getValue()}</div>;
            }
          }
        ]
      : [
          {
            header: t('detailed_instructions') as string,
            id: 'detailedInstructions',
            isEnableSorting: false,
            isSelected: true,
            renderer: (value: CellContext<TableColumnConfigs, string>) => {
              const data = value.getValue();
              const isEmpty = data === 'None';
              return <Description isEmpty={isEmpty}>{isEmpty ? 'N/A' : data}</Description>;
            }
          },
          {
            header: t('mm_table_status') as string,
            id: 'suggestedDue',
            isEnableSorting: true,
            isSelected: true,
            renderer: (value: CellContext<TableColumnConfigs, string>) => {
              const data = value.row.original as MaintenanceEventTableRow;
              const { cell, cellColor } = getStatusCellEvent(data, theme, t);
              return maybeEmptyCell(
                data as TableRow,
                cell,
                cellColor,
                '',
                `1rem .5rem 1rem ${cellColor === 'transparent' ? '0rem' : '0.75rem'}`,
                'auto'
              );
            }
          },
          {
            header: t('mm_table_next_step') as string,
            id: 'nextStep',
            isEnableSorting: false,
            isSelected: true,
            renderer: (value: CellContext<TableColumnConfigs, string>) => {
              const data = value.row.original;
              // TODO - provide subcomponent when available from backend
              const { cell } = getNextStepCell(
                data as MaintenanceEventTableRow,
                theme,
                t,
                setSelectedPm,
                onClickCreateService
              );
              return maybeEmptyCell(
                data as TableRow,
                cell,
                '',
                theme.colors.primaryBlue5,
                '',
                'auto'
              );
            }
          }
        ])
  ];
};
export { generateColumnConfigs };
